import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchUsers,
  createUser as createUserThunk,
  updateUser as updateUserThunk,
  deleteUser as deleteUserThunk,
} from '../../store/slices/usersSlice';
import apiService from '../../services/api';
import { subscribeToStoreUpdates, subscribeToUsersUpdates } from '../../services/socket';
import {
  Settings as SettingsIcon, Users, Info, Plus, Edit, Trash2,
  MapPin, ScanLine, Printer, Phone, Share2, Check, Loader2, Copy,
  Eye, EyeOff, Download, X, FileText, Monitor, Truck, Store,
  Receipt, Lock, Search, RefreshCw,
} from 'lucide-react';
import JsBarcode from 'jsbarcode';
import { useLanguage } from '../../contexts/LanguageContext';
import { useLatinKeyboardGroupProps } from '../../hooks/useLatinKeyboardGroupProps';
import { useToast, useConfirm } from '../../contexts/ToastContext';
import { getImageUrl, convertImageTo1BitMonochrome, persistStoreLogoPath } from '../../utils/format';
import { printHtml } from '../../utils/printing';
import { getTokens } from '../../utils/tokens';
import { normalizeForSearch } from '../../utils/searchUtils';
import AboutContent from '../common/AboutContent';
import {
  isAdminOrOwner,
  isManagerOrAbove,
  canAssignRole,
  canEditTargetUser,
  canDeleteTargetUser,
  canToggleTargetUserStatus,
  isUserVisibleInList,
} from '../../lib/roles';
import { getAdminSelectCSS, getAdminFormSelectCSS, getAdminFieldSelectCSS } from '../../utils/adminSelect';
import { PageHeader, PrimaryBtn, GhostBtn, AvatarInitials, StatusPill } from '../ui/AdminPagePrimitives';
import en from '../../translations/en';
import ar from '../../translations/ar';

const barcodeTranslations = { en, ar };
const getBarcodeT = (lang) => (key) => {
  const keys = key.split('.');
  let value = barcodeTranslations[lang] || barcodeTranslations.en;
  for (const k of keys) value = value?.[k];
  return value || key;
};

// ─── Internal helpers ─────────────────────────────────────────────────────────

function SettingsCard({ tk, title, subtitle, headerRight, children }) {
  const hasHeaderRow = Boolean(title || headerRight);
  /** Subtitle under title only; wide header row is title + optional headerRight (member count stays with title). */
  const subtitleBesideActions = Boolean(subtitle && headerRight);
  const subtitleStandalone = Boolean(subtitle && !headerRight);
  const headerRowMarginBottom = subtitleStandalone ? 4 : subtitle ? 16 : 4;

  return (
    <div style={{
      background: tk.surface, border: `1px solid ${tk.border}`,
      borderRadius: 12, padding: 20, marginBottom: 12,
    }}>
      {hasHeaderRow && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
          marginBottom: headerRowMarginBottom,
        }}
        >
          {(title || subtitleBesideActions) && (
            <div style={{ flex: 1, minWidth: 0 }}>
              {title && (
                <div style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: tk.text,
                  lineHeight: 1.25,
                  ...(headerRight ? { paddingTop: 2 } : {}),
                }}
                >
                  {title}
                </div>
              )}
              {subtitleBesideActions && (
                <div style={{ fontSize: 12, color: tk.textMuted, marginTop: title ? 4 : 0 }}>
                  {subtitle}
                </div>
              )}
            </div>
          )}
          {headerRight && <div style={{ flexShrink: 0 }}>{headerRight}</div>}
        </div>
      )}
      {subtitleStandalone && (
        <div style={{ fontSize: 12, color: tk.textMuted, marginBottom: 16 }}>{subtitle}</div>
      )}
      {children}
    </div>
  );
}

function SettingField({ tk, label, value, onChange, type = 'text', placeholder, disabled, maxLength, children }) {
  const fieldStyle = {
    width: '100%', height: 36, padding: '0 12px',
    background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
    borderRadius: 7, color: tk.text, fontFamily: 'inherit',
    fontSize: 12.5, outline: 'none', boxSizing: 'border-box',
    opacity: disabled ? 0.5 : 1,
  };
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      {children || (
        <input type={type} value={value} onChange={onChange} placeholder={placeholder} disabled={disabled} maxLength={maxLength} style={fieldStyle} />
      )}
    </div>
  );
}

function SettingToggle({ tk, label, value, onChange, last, help }) {
  return (
    <div style={{ padding: '12px 0', borderBottom: last ? 'none' : `1px solid ${tk.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12.5, color: tk.text }}>{label}</span>
        <button type="button" onClick={onChange} style={{ width: 36, height: 20, borderRadius: 99, background: value ? tk.accent : tk.sunken, position: 'relative', border: 'none', cursor: 'pointer', transition: 'background 0.15s', flexShrink: 0 }}>
          <div style={{ position: 'absolute', top: 2, left: value ? 18 : 2, width: 16, height: 16, borderRadius: 99, background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.15s' }} />
        </button>
      </div>
      {help ? (
        <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 6, lineHeight: 1.4 }}>{help}</div>
      ) : null}
    </div>
  );
}

function SelectField({ tk, label, value, onChange, options, disabled }) {
  const sel = getAdminFieldSelectCSS(tk, 'set-sel');
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </div>
      <style>{sel.css}</style>
      <select value={value} onChange={onChange} disabled={disabled} className={sel.className} style={{ opacity: disabled ? 0.5 : 1 }}>
        {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
      </select>
    </div>
  );
}

function ModalOverlay({ children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }}>
      {children}
    </div>
  );
}

function ModalBox({ tk, children }) {
  return (
    <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 14, padding: 24, maxWidth: 480, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
      {children}
    </div>
  );
}

// ─── GeneralSettings (extracted to prevent remount) ───────────────────────────

const GeneralSettings = ({ storeData, tk, t, isRTL }) => {
  const showToast = useToast();
  const showConfirm = useConfirm();
  const settingsFieldSel = getAdminFieldSelectCSS(tk, 'set-field');
  const [form, setForm] = useState({
    name: storeData?.name ?? '',
    address: storeData?.address ?? '',
    receipt_footer: storeData?.receipt_footer ?? '',
    low_stock_threshold: storeData?.low_stock_threshold ?? 10,
    include_delivery_fee: storeData?.include_delivery_fee ?? false,
    requires_ref_num: storeData?.requires_ref_num ?? true,
    discount_requires_approval: storeData?.discount_requires_approval !== false,
    allow_negative_stock: storeData?.allow_negative_stock ?? false,
    logo_path: storeData?.logo_path ?? '',
    logo_size: storeData?.logo_size ?? 100,
    refund_period: storeData?.refund_period ?? 1,
    tax: storeData?.tax ?? 0,
  });
  const [website, setWebsite] = useState({ url: '', include_in_receipt: 'none' });
  const [deliveryFees, setDeliveryFees] = useState([]);
  const [editingRegion, setEditingRegion] = useState(null);
  const [newRegion, setNewRegion] = useState('');
  const [newFee, setNewFee] = useState('');
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [editingPhone, setEditingPhone] = useState(null);
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [newPhoneIncludeReceipt, setNewPhoneIncludeReceipt] = useState(true);
  const [socialMediaUrls, setSocialMediaUrls] = useState([]);
  const [editingSocial, setEditingSocial] = useState(null);
  const [newSocialPlatform, setNewSocialPlatform] = useState('facebook');
  const [newSocialUrl, setNewSocialUrl] = useState('');
  const [newSocialIncludeReceipt, setNewSocialIncludeReceipt] = useState(true);
  const [promos, setPromos] = useState([]);
  const [newPromoName, setNewPromoName] = useState('');
  const [newPromoPercentage, setNewPromoPercentage] = useState('');
  const [editingPromo, setEditingPromo] = useState(null);
  const [saveStatus, setSaveStatus] = useState('');
  const saveTimeoutRef = useRef(null);
  const statusTimeoutRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const hasLoadedDataRef = useRef(false);
  const hasPendingChangesRef = useRef(false);
  const performSaveRef = useRef(null);

  const inputStyle = {
    width: '100%', height: 36, padding: '0 12px',
    background: tk.surfaceAlt, border: `1px solid ${tk.border}`,
    borderRadius: 7, color: tk.text, fontFamily: 'inherit',
    fontSize: 12.5, outline: 'none', boxSizing: 'border-box',
  };
  const btnSmall = (variant = 'ghost') => ({
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '0 10px', height: 30, borderRadius: 6,
    fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'pointer',
    background: variant === 'primary' ? tk.accent : variant === 'danger' ? tk.dangerSoft : tk.surfaceAlt,
    color: variant === 'primary' ? '#fff' : variant === 'danger' ? tk.dangerText : tk.textSecondary,
    border: `1px solid ${variant === 'primary' ? 'transparent' : variant === 'danger' ? tk.dangerText + '44' : tk.border}`,
  });

  useEffect(() => {
    if (storeData && !hasLoadedDataRef.current) {
      hasLoadedDataRef.current = true;
      setForm({
        name: storeData.name ?? '',
        address: storeData.address ?? '',
        receipt_footer: storeData.receipt_footer ?? '',
        low_stock_threshold: storeData.low_stock_threshold ?? 10,
        include_delivery_fee: storeData.include_delivery_fee ?? false,
        requires_ref_num: storeData.requires_ref_num ?? true,
        discount_requires_approval: storeData.discount_requires_approval !== false,
        allow_negative_stock: storeData.allow_negative_stock ?? false,
        logo_path: storeData.logo_path ?? '',
        logo_size: storeData.logo_size ?? 100,
        refund_period: storeData.refund_period ?? 1,
        tax: storeData.tax ?? 0,
      });
      let websiteObj = storeData.website;
      if (typeof websiteObj === 'string') {
        try { const parsed = JSON.parse(websiteObj); websiteObj = parsed && typeof parsed === 'object' ? parsed : null; } catch { websiteObj = { url: websiteObj, include_in_receipt: 'none' }; }
      }
      if (websiteObj && typeof websiteObj === 'object') {
        setWebsite({ url: websiteObj.url || '', include_in_receipt: websiteObj.include_in_receipt || 'none' });
      } else {
        setWebsite({ url: '', include_in_receipt: 'none' });
      }
      if (storeData.delivery_fee && typeof storeData.delivery_fee === 'object') {
        setDeliveryFees(Object.entries(storeData.delivery_fee).map(([region, fee]) => ({ region, fee })));
      } else { setDeliveryFees([]); }
      if (storeData.phone_numbers && typeof storeData.phone_numbers === 'object') {
        const phones = Object.entries(storeData.phone_numbers).map(([key, value]) => {
          if (typeof value === 'object' && value !== null && 'number' in value) return { number: value.number || key, include_in_receipt: value.include_in_receipt !== false };
          return { number: key, include_in_receipt: typeof value === 'boolean' ? value : true };
        }).filter((p) => p.number);
        setPhoneNumbers(phones);
      } else { setPhoneNumbers([]); }
      if (storeData.social_media_urls && typeof storeData.social_media_urls === 'object') {
        setSocialMediaUrls(Object.entries(storeData.social_media_urls).map(([platform, value]) => {
          if (typeof value === 'object' && value !== null) return { platform, url: value.url || value, include_in_receipt: value.include_in_receipt !== false };
          return { platform, url: value, include_in_receipt: true };
        }));
      } else { setSocialMediaUrls([]); }
      if (storeData.promo && typeof storeData.promo === 'object' && !Array.isArray(storeData.promo)) {
        const name = String(storeData.promo.name || '').trim();
        const pct = parseFloat(storeData.promo.percentage);
        if (name && Number.isFinite(pct) && pct > 0) {
          setPromos([{ name, percentage: pct }]);
        } else {
          setPromos([]);
        }
      } else {
        setPromos([]);
      }
      setNewPromoName('');
      setNewPromoPercentage('');
      setEditingPromo(null);
      setTimeout(() => { isInitialLoadRef.current = false; }, 100);
    }
  }, [storeData]);

  const handleAddPromo = () => {
    if (!newPromoName.trim() || !newPromoPercentage.trim()) {
      showToast(t('settings.promoEnterBoth'), 'warning');
      return;
    }
    if (newPromoName.trim().length > 20) {
      showToast(t('settings.promoNameMaxLength'), 'warning');
      return;
    }
    const pct = parseFloat(newPromoPercentage);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      showToast(t('settings.promoPercentageInvalid'), 'warning');
      return;
    }
    if (promos.length >= 1) {
      showToast(t('settings.promoOnlyOne'), 'warning');
      return;
    }
    setPromos([{ name: newPromoName.trim(), percentage: pct }]);
    setNewPromoName('');
    setNewPromoPercentage('');
  };
  const handleEditPromo = (index) => {
    const p = promos[index];
    setEditingPromo(index);
    setNewPromoName(p.name);
    setNewPromoPercentage(String(p.percentage));
  };
  const handleUpdatePromo = () => {
    if (!newPromoName.trim() || !newPromoPercentage.trim()) {
      showToast(t('settings.promoEnterBoth'), 'warning');
      return;
    }
    if (newPromoName.trim().length > 20) {
      showToast(t('settings.promoNameMaxLength'), 'warning');
      return;
    }
    const pct = parseFloat(newPromoPercentage);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      showToast(t('settings.promoPercentageInvalid'), 'warning');
      return;
    }
    const updated = [...promos];
    updated[editingPromo] = { name: newPromoName.trim(), percentage: pct };
    setPromos(updated);
    setEditingPromo(null);
    setNewPromoName('');
    setNewPromoPercentage('');
  };
  const handleDeletePromo = async (index) => {
    if (!await showConfirm(t('settings.deletePromoConfirm'), {
      confirmLabel: t('common.delete') || 'Delete',
      cancelLabel: t('common.cancel') || 'Cancel',
      type: 'danger',
    })) return;
    setPromos(promos.filter((_, i) => i !== index));
  };

  const handlePromoPercentageChange = (e) => {
    let val = e.target.value;
    val = val.replace(/[^0-9.]/g, '');
    const parts = val.split('.');
    if (parts.length > 2) {
      val = parts[0] + '.' + parts.slice(1).join('');
    }
    if (parts.length === 2 && parts[1].length > 2) {
      val = parts[0] + '.' + parts[1].slice(0, 2);
    }
    const num = parseFloat(val);
    if (!isNaN(num) && num > 100) {
      val = '100';
    }
    setNewPromoPercentage(val);
  };

  const handlePromoPercentageBlur = () => {
    const num = parseFloat(newPromoPercentage);
    if (isNaN(num) || num < 0) {
      setNewPromoPercentage('');
    } else {
      setNewPromoPercentage(String(Math.min(100, num)));
    }
  };

  const handleAddRegion = () => {
    if (!newRegion.trim() || !newFee.trim()) { showToast(t('alerts.enterBothRegionAndFee'), "warning"); return; }
    if (newRegion.trim().length > 20) { showToast(t('alerts.regionNameMaxLength'), "warning"); return; }
    const fee = parseFloat(newFee);
    if (isNaN(fee) || fee < 0) { showToast(t('alerts.feeMustBePositive'), "warning"); return; }
    if (deliveryFees.some((f) => f.region.toLowerCase() === newRegion.trim().toLowerCase())) { showToast(t('alerts.regionAlreadyExists'), "warning"); return; }
    setDeliveryFees([...deliveryFees, { region: newRegion.trim(), fee }]);
    setNewRegion(''); setNewFee('');
  };
  const handleEditRegion = (index) => { const f = deliveryFees[index]; setEditingRegion(index); setNewRegion(f.region); setNewFee(f.fee.toString()); };
  const handleUpdateRegion = () => {
    if (!newRegion.trim() || !newFee.trim()) { showToast(t('alerts.enterBothRegionAndFee'), "warning"); return; }
    if (newRegion.trim().length > 20) { showToast(t('alerts.regionNameMaxLength'), "warning"); return; }
    const fee = parseFloat(newFee);
    if (isNaN(fee) || fee < 0) { showToast(t('alerts.feeMustBePositive'), "warning"); return; }
    const updated = [...deliveryFees]; updated[editingRegion] = { region: newRegion.trim(), fee };
    setDeliveryFees(updated); setEditingRegion(null); setNewRegion(''); setNewFee('');
  };
  const handleDeleteRegion = async (index) => { if (!await showConfirm(t('alerts.deleteDeliveryRegion'), { confirmLabel: t('common.delete') || 'Delete', cancelLabel: t('common.cancel') || 'Cancel', type: 'danger' })) return; setDeliveryFees(deliveryFees.filter((_, i) => i !== index)); };

  const handleAddPhone = () => {
    if (!newPhoneNumber.trim()) { showToast(t('alerts.pleaseEnterPhoneNumber'), "warning"); return; }
    if (newPhoneNumber.trim().length > 15) { showToast(t('alerts.phoneNumberMaxLength'), "warning"); return; }
    const trimmed = newPhoneNumber.trim();
    if (phoneNumbers.some((p) => p.number === trimmed)) { showToast(t('alerts.phoneNumberAlreadyExists'), "warning"); return; }
    setPhoneNumbers([...phoneNumbers, { number: trimmed, include_in_receipt: newPhoneIncludeReceipt }]);
    setNewPhoneNumber(''); setNewPhoneIncludeReceipt(true);
  };
  const handleEditPhone = (index) => { const p = phoneNumbers[index]; setEditingPhone(index); setNewPhoneNumber(p.number); setNewPhoneIncludeReceipt(p.include_in_receipt); };
  const handleUpdatePhone = () => {
    if (!newPhoneNumber.trim()) { showToast(t('alerts.pleaseEnterPhoneNumber'), "warning"); return; }
    if (newPhoneNumber.trim().length > 15) { showToast(t('alerts.phoneNumberMaxLength'), "warning"); return; }
    const trimmed = newPhoneNumber.trim();
    if (phoneNumbers.some((p, i) => i !== editingPhone && p.number === trimmed)) { showToast(t('alerts.phoneNumberAlreadyExists'), "warning"); return; }
    const updated = [...phoneNumbers]; updated[editingPhone] = { ...updated[editingPhone], number: trimmed, include_in_receipt: newPhoneIncludeReceipt };
    setPhoneNumbers(updated); setEditingPhone(null); setNewPhoneNumber(''); setNewPhoneIncludeReceipt(true);
  };
  const handleDeletePhone = async (index) => { if (!await showConfirm(t('alerts.deletePhoneNumber'), { confirmLabel: t('common.delete') || 'Delete', cancelLabel: t('common.cancel') || 'Cancel', type: 'danger' })) return; setPhoneNumbers(phoneNumbers.filter((_, i) => i !== index)); };
  const handleTogglePhoneReceipt = (index) => { const updated = [...phoneNumbers]; updated[index].include_in_receipt = !updated[index].include_in_receipt; setPhoneNumbers(updated); };

  const handleAddSocial = () => {
    if (!newSocialUrl.trim()) { showToast(t('alerts.pleaseEnterUrl'), "warning"); return; }
    if (newSocialUrl.trim().length > 70) { showToast(t('alerts.socialUrlMaxLength'), "warning"); return; }
    if (socialMediaUrls.some((s) => s.platform === newSocialPlatform)) { showToast(t('alerts.platformAlreadyExists'), "warning"); return; }
    setSocialMediaUrls([...socialMediaUrls, { platform: newSocialPlatform, url: newSocialUrl.trim(), include_in_receipt: newSocialIncludeReceipt }]);
    setNewSocialPlatform('facebook'); setNewSocialUrl(''); setNewSocialIncludeReceipt(true);
  };
  const handleEditSocial = (index) => { const s = socialMediaUrls[index]; setEditingSocial(index); setNewSocialPlatform(s.platform); setNewSocialUrl(s.url); setNewSocialIncludeReceipt(s.include_in_receipt); };
  const handleUpdateSocial = () => {
    if (!newSocialUrl.trim()) { showToast(t('alerts.pleaseEnterUrl'), "warning"); return; }
    if (newSocialUrl.trim().length > 70) { showToast(t('alerts.socialUrlMaxLength'), "warning"); return; }
    const updated = [...socialMediaUrls]; const oldPlatform = updated[editingSocial].platform;
    if (oldPlatform !== newSocialPlatform && socialMediaUrls.some((s, i) => i !== editingSocial && s.platform === newSocialPlatform)) { showToast(t('alerts.platformAlreadyExists'), "warning"); return; }
    updated[editingSocial] = { platform: newSocialPlatform, url: newSocialUrl.trim(), include_in_receipt: newSocialIncludeReceipt };
    setSocialMediaUrls(updated); setEditingSocial(null); setNewSocialPlatform('facebook'); setNewSocialUrl(''); setNewSocialIncludeReceipt(true);
  };
  const handleDeleteSocial = async (index) => { if (!await showConfirm(t('alerts.deleteSocialMedia'), { confirmLabel: t('common.delete') || 'Delete', cancelLabel: t('common.cancel') || 'Cancel', type: 'danger' })) return; setSocialMediaUrls(socialMediaUrls.filter((_, i) => i !== index)); };
  const handleToggleSocialReceipt = (index) => { const updated = [...socialMediaUrls]; updated[index].include_in_receipt = !updated[index].include_in_receipt; setSocialMediaUrls(updated); };

  const performSave = useCallback(async () => {
    hasPendingChangesRef.current = false;
    if ((form.name || '').length > 20) { showToast(t('alerts.storeNameMaxLength'), "warning"); return; }
    if ((form.address || '').length > 50) { showToast(t('alerts.addressMaxLength'), "warning"); return; }
    if ((website.url || '').length > 40) { showToast(t('alerts.websiteMaxLength'), "warning"); return; }
    if ((form.receipt_footer || '').length > 50) { showToast(t('alerts.receiptFooterMaxLength'), "warning"); return; }
    const logoSz = parseInt(form.logo_size, 10);
    if (!Number.isFinite(logoSz) || logoSz < 40 || logoSz > 400) { showToast(t('settings.logoSizeInvalid'), "warning"); return; }
    const refundDays = parseInt(form.refund_period, 10);
    if (!Number.isFinite(refundDays) || refundDays < 1 || refundDays > 365) { showToast(t('settings.refundPeriodInvalid'), "warning"); return; }
    for (const { region } of deliveryFees) { if ((region || '').length > 20) { showToast(t('alerts.regionNameMaxLength'), "warning"); return; } }
    for (const { number } of phoneNumbers) { if ((number || '').length > 15) { showToast(t('alerts.phoneNumberMaxLength'), "warning"); return; } }
    for (const { url } of socialMediaUrls) { if ((url || '').length > 70) { showToast(t('alerts.socialUrlMaxLength'), "warning"); return; } }
    let promoPayload = null;
    if (promos.length > 0) {
      const p = promos[0];
      if ((p.name || '').length > 20) { showToast(t('settings.promoNameMaxLength'), 'warning'); return; }
      const pct = parseFloat(p.percentage);
      if (!Number.isFinite(pct) || pct <= 0 || pct > 100) { showToast(t('settings.promoPercentageInvalid'), 'warning'); return; }
      promoPayload = { name: p.name, percentage: pct };
    }
    setSaveStatus('saving');
    try {
      const logoSizePx = Math.min(400, Math.max(40, parseInt(form.logo_size, 10) || 100));
      let logoPath = form.logo_path || null;
      try {
        logoPath = await persistStoreLogoPath(logoPath);
        if (logoPath !== form.logo_path) {
          setForm((f) => ({ ...f, logo_path: logoPath || '' }));
        }
      } catch (logoErr) {
        setSaveStatus('');
        showToast(logoErr.message || t('alerts.failedToSave'), 'warning');
        return;
      }
      const deliveryFeeObj = {};
      deliveryFees.forEach(({ region, fee }) => { deliveryFeeObj[region] = fee; });
      const phoneNumbersObj = {};
      phoneNumbers.forEach(({ number, include_in_receipt }) => { phoneNumbersObj[number] = include_in_receipt; });
      const socialMediaUrlsObj = {};
      socialMediaUrls.forEach(({ platform, url, include_in_receipt }) => { socialMediaUrlsObj[platform] = { url, include_in_receipt }; });
      await apiService.updateStore({
        ...form,
        delivery_fee: deliveryFees.length > 0 ? deliveryFeeObj : null,
        phone_numbers: phoneNumbers.length > 0 ? phoneNumbersObj : null,
        social_media_urls: socialMediaUrls.length > 0 ? socialMediaUrlsObj : null,
        website: website.url ? { url: website.url, include_in_receipt: website.include_in_receipt } : null,
        low_stock_threshold: form.low_stock_threshold || 10,
        include_delivery_fee: form.include_delivery_fee || false,
        requires_ref_num: form.requires_ref_num !== false,
        discount_requires_approval: form.discount_requires_approval !== false,
        allow_negative_stock: form.allow_negative_stock === true,
        logo_path: logoPath,
        logo_size: logoSizePx,
        refund_period: Math.min(365, Math.max(1, parseInt(form.refund_period, 10) || 1)),
        tax: form.tax ?? 0,
        promo: promoPayload,
      });
      setSaveStatus('saved');
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
      statusTimeoutRef.current = setTimeout(() => setSaveStatus(''), 2000);
    } catch (e) {
      setSaveStatus('');
      showToast(e.message || t('alerts.failedToSave').replace('{error}', t('alerts.unknownError')), "warning");
    }
  }, [form, deliveryFees, phoneNumbers, socialMediaUrls, website, promos, t]);

  useEffect(() => { performSaveRef.current = performSave; }, [performSave]);

  const triggerAutoSave = useCallback(() => {
    if (isInitialLoadRef.current) return;
    hasPendingChangesRef.current = true;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => { performSaveRef.current?.(); }, 800);
  }, []);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current);
      if (hasPendingChangesRef.current && performSaveRef.current) performSaveRef.current();
    };
  }, []);

  useEffect(() => {
    if (isInitialLoadRef.current) return;
    triggerAutoSave();
  }, [form, website, deliveryFees, phoneNumbers, socialMediaUrls, promos]);

  return (
    <>
      <style>{settingsFieldSel.css}</style>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Store information */}
      <SettingsCard
        tk={tk}
        title={t('settings.storeInformation')}
        subtitle={t('settings.storeInformationSubtitle') || 'Appears on receipts and reports'}
        headerRight={
          (saveStatus === 'saving' || saveStatus === 'saved') && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
              {saveStatus === 'saving' && <><Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /><span style={{ color: tk.textMuted }}>{t('settings.saving')}</span></>}
              {saveStatus === 'saved' && <><Check size={12} /><span style={{ color: tk.successText }}>{t('settings.saved')}</span></>}
            </div>
          )
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, alignItems: 'start' }}>
          <SettingField tk={tk} label={t('settings.storeName')} value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} maxLength={20} placeholder={t('settings.storeName')} />

          {/* Logo upload */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('settings.storeLogo')}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {form.logo_path && (
                <div style={{ position: 'relative', width: 52, height: 52, borderRadius: 8, overflow: 'hidden', border: `1px solid ${tk.border}`, flexShrink: 0 }}>
                  <img src={getImageUrl(form.logo_path)} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  <button type="button" onClick={() => setForm((f) => ({ ...f, logo_path: '' }))} style={{ position: 'absolute', top: 0, right: 0, width: 18, height: 18, background: tk.danger, color: '#fff', border: 'none', borderRadius: '0 0 0 4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>×</button>
                </div>
              )}
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 12px', height: 36, borderRadius: 7, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, cursor: 'pointer', fontSize: 12.5 }}>
                <Plus size={13} />
                {form.logo_path ? t('settings.changeLogo') : t('settings.uploadLogo')}
                <input type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onloadend = async () => {
                        let imageData = reader.result;
                        try {
                          imageData = await convertImageTo1BitMonochrome(imageData, 128);
                        } catch (err) {
                          console.error('Logo monochrome conversion failed:', err);
                        }
                        const pngName = (file.name || 'logo').replace(/\.[^.]+$/, '') + '.png';
                        try {
                          const res = await apiService.uploadImage(imageData, pngName, 'store');
                          setForm((f) => ({ ...f, logo_path: res.url || res.path }));
                        } catch (err) {
                          console.error('Failed to save logo:', err);
                          setForm((f) => ({ ...f, logo_path: imageData }));
                        }
                      };
                      reader.readAsDataURL(file);
                    }
                    e.target.value = '';
                  }}
                />
              </label>
            </div>
            <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 4 }}>{t('settings.logoHelp')}</div>
          </div>

          <SettingField tk={tk} label={t('settings.address')} value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} maxLength={50} placeholder={t('settings.address')} />

          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('settings.logoSize')}</div>
            <input
              type="number"
              min={40}
              max={400}
              value={form.logo_size ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, logo_size: v === '' ? '' : parseInt(v, 10) }));
              }}
              onBlur={() => {
                setForm((f) => {
                  const n = parseInt(f.logo_size, 10);
                  if (!Number.isFinite(n)) return { ...f, logo_size: 40 };
                  if (n < 40) return { ...f, logo_size: 40 };
                  if (n > 400) return { ...f, logo_size: 400 };
                  return { ...f, logo_size: n };
                });
              }}
              style={inputStyle}
            />
            <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 4 }}>{t('settings.logoSizeHelp')}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <SettingField tk={tk} label={t('settings.receiptFooter')} value={form.receipt_footer} onChange={(e) => setForm((f) => ({ ...f, receipt_footer: e.target.value }))} maxLength={50} placeholder={t('receipt.thankYou')} />
          <SettingField tk={tk} label={t('settings.websiteUrl')} value={website.url} onChange={(e) => setWebsite((w) => ({ ...w, url: e.target.value }))} type="url" maxLength={40} placeholder="https://example.com" />
          <SelectField tk={tk} label={t('settings.websiteOnReceipt')} value={website.include_in_receipt} onChange={(e) => setWebsite((w) => ({ ...w, include_in_receipt: e.target.value }))}
            options={[{ value: 'none', label: t('settings.dontInclude') }, { value: 'link', label: t('settings.showAsLink') }, { value: 'qr', label: t('settings.showAsQr') }]}
          />
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('settings.lowStockThreshold')}</div>
            <input type="text" inputMode="numeric" pattern="[0-9]*" value={form.low_stock_threshold} onChange={(e) => { const v = e.target.value.replace(/\D/g, ''); setForm((f) => ({ ...f, low_stock_threshold: Math.min(parseInt(v) || 0, 999999) })); }} style={inputStyle} placeholder="10" />
            <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 4 }}>{t('settings.lowStockThresholdHelp')}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('settings.tax')}</div>
            <input type="number" value={form.tax === 0 ? '' : form.tax} onChange={(e) => setForm((f) => ({ ...f, tax: Math.min(Math.max(parseFloat(e.target.value) || 0, 0), 100) }))} style={inputStyle} placeholder="0" min="0" max="100" step="0.01" />
          </div>
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: tk.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('settings.refundPeriod')}</div>
            <input type="number" min={1} max={365} value={form.refund_period} onChange={(e) => { const n = parseInt(e.target.value, 10); setForm((f) => ({ ...f, refund_period: Number.isFinite(n) ? Math.min(365, Math.max(1, n)) : 1 })); }} style={inputStyle} />
            <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 4 }}>{t('settings.refundPeriodHelp')}</div>
          </div>
        </div>
        <div style={{ marginTop: 14 }}>
          <SettingToggle
            tk={tk}
            label={t('settings.includeDeliveryFeeInSales')}
            value={form.include_delivery_fee}
            onChange={() => setForm((f) => ({ ...f, include_delivery_fee: !f.include_delivery_fee }))}
            help={t('settings.deliveryFeeIncludedHelp')}
          />
          <SettingToggle tk={tk} label={t('settings.requireRefNumNonCash')} value={form.requires_ref_num} onChange={() => setForm((f) => ({ ...f, requires_ref_num: !f.requires_ref_num }))} />
          <SettingToggle tk={tk} label={t('settings.discountRequiresApproval')} value={form.discount_requires_approval} onChange={() => setForm((f) => ({ ...f, discount_requires_approval: !f.discount_requires_approval }))} />
          <SettingToggle tk={tk} label={t('settings.allowNegativeStock')} value={form.allow_negative_stock} onChange={() => setForm((f) => ({ ...f, allow_negative_stock: !f.allow_negative_stock }))} help={t('settings.allowNegativeStockHelp')} last />
        </div>
      </SettingsCard>

      <SettingsCard tk={tk} title={t('settings.storePromo')} subtitle={t('settings.storePromoHelp')}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <input
            type="text"
            value={newPromoName}
            onChange={(e) => setNewPromoName(e.target.value)}
            placeholder={t('settings.promoNamePlaceholder')}
            maxLength={20}
            style={{ ...inputStyle, flex: 1, minWidth: 120 }}
          />
          <input
            type="text"
            inputMode="decimal"
            value={newPromoPercentage}
            onChange={handlePromoPercentageChange}
            onBlur={handlePromoPercentageBlur}
            placeholder={t('settings.promoPercentage')}
            style={{ ...inputStyle, width: 140, flex: 'none', minWidth: 120, fontFamily: '"JetBrains Mono", monospace' }}
          />
          {editingPromo === null ? (
            <button
              type="button"
              onClick={handleAddPromo}
              disabled={promos.length >= 1}
              style={{ ...btnSmall('primary'), opacity: promos.length >= 1 ? 0.5 : 1, cursor: promos.length >= 1 ? 'not-allowed' : 'pointer' }}
            >
              <Plus size={12} />
              {t('common.add')}
            </button>
          ) : (
            <>
              <button type="button" onClick={handleUpdatePromo} style={{ ...btnSmall('primary'), background: tk.success }}>
                {t('common.update')}
              </button>
              <button
                type="button"
                onClick={() => { setEditingPromo(null); setNewPromoName(''); setNewPromoPercentage(''); }}
                style={btnSmall()}
              >
                {t('common.cancel')}
              </button>
            </>
          )}
        </div>
        {promos.length > 0 ? (
          <div style={{ border: `1px solid ${tk.border}`, borderRadius: 8, overflow: 'hidden' }}>
            {promos.map((promo, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '10px 14px',
                  gap: 12,
                  background: index % 2 === 0 ? 'transparent' : tk.surfaceAlt,
                }}
              >
                <span style={{ flex: 1, fontSize: 12.5, color: tk.text }}>{promo.name}</span>
                <span style={{ fontSize: 12, fontFamily: '"JetBrains Mono",monospace', color: tk.textSecondary }}>
                  {promo.percentage}%
                </span>
                <button
                  type="button"
                  onClick={() => handleEditPromo(index)}
                  disabled={editingPromo !== null}
                  style={{ ...btnSmall(), padding: '0 8px' }}
                >
                  <Edit size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDeletePromo(index)}
                  disabled={editingPromo !== null}
                  style={{ ...btnSmall('danger'), padding: '0 8px' }}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 12 }}>
            {t('settings.noStorePromo')}
          </div>
        )}
      </SettingsCard>

      {/* Delivery regions */}
      <SettingsCard tk={tk} title={t('settings.deliveryRegionsFees')} subtitle={t('settings.deliveryFeeSettings')}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <input type="text" value={newRegion} onChange={(e) => setNewRegion(e.target.value)} placeholder={t('settings.regionName')} maxLength={20} style={{ ...inputStyle, flex: 1, minWidth: 120 }} />
          <input type="number" value={newFee} onChange={(e) => setNewFee(e.target.value)} placeholder={t('settings.feeEgp')} step="0.01" min="0" style={{ ...inputStyle, width: 120, flex: 'none' }} />
          {editingRegion === null ? (
            <button onClick={handleAddRegion} style={btnSmall('primary')}><Plus size={12} />{t('common.add')}</button>
          ) : (
            <>
              <button onClick={handleUpdateRegion} style={{ ...btnSmall('primary'), background: tk.success }}>{t('common.update')}</button>
              <button onClick={() => { setEditingRegion(null); setNewRegion(''); setNewFee(''); }} style={btnSmall()}>{t('common.cancel')}</button>
            </>
          )}
        </div>
        {deliveryFees.length > 0 ? (
          <div style={{ border: `1px solid ${tk.border}`, borderRadius: 8, overflow: 'hidden' }}>
            {deliveryFees.map((fee, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', gap: 12, borderBottom: index < deliveryFees.length - 1 ? `1px solid ${tk.border}` : 'none', background: index % 2 === 0 ? 'transparent' : tk.surfaceAlt }}>
                <span style={{ flex: 1, fontSize: 12.5, color: tk.text }}>{fee.region}</span>
                <span style={{ fontSize: 12, fontFamily: '"JetBrains Mono",monospace', color: tk.textSecondary }}>{t('currency.egp')} {parseFloat(fee.fee).toLocaleString()}</span>
                <button onClick={() => handleEditRegion(index)} disabled={editingRegion !== null} style={{ ...btnSmall(), padding: '0 8px' }}><Edit size={12} /></button>
                <button onClick={() => handleDeleteRegion(index)} disabled={editingRegion !== null} style={{ ...btnSmall('danger'), padding: '0 8px' }}><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 12 }}>{t('settings.noDeliveryRegions')}</div>
        )}
      </SettingsCard>

      {/* Phone numbers */}
      <SettingsCard tk={tk} title={t('settings.phoneNumbers')}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <input type="tel" value={newPhoneNumber} onChange={(e) => setNewPhoneNumber(e.target.value.replace(/\D/g, ''))} placeholder={t('settings.phoneNumber')} maxLength={15} style={{ ...inputStyle, flex: 1, minWidth: 150 }} />
          <button onClick={() => setNewPhoneIncludeReceipt((v) => !v)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0 10px', height: 36, background: newPhoneIncludeReceipt ? tk.accentSoft : tk.surfaceAlt, color: newPhoneIncludeReceipt ? tk.accentText : tk.textMuted, border: `1px solid ${tk.border}`, borderRadius: 7, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
            <Check size={12} style={{ opacity: newPhoneIncludeReceipt ? 1 : 0.3 }} />{t('settings.includeInReceipt')}
          </button>
          {editingPhone === null ? (
            <button onClick={handleAddPhone} style={btnSmall('primary')}><Plus size={12} />{t('common.add')}</button>
          ) : (
            <>
              <button onClick={handleUpdatePhone} style={{ ...btnSmall('primary'), background: tk.success }}>{t('common.update')}</button>
              <button onClick={() => { setEditingPhone(null); setNewPhoneNumber(''); setNewPhoneIncludeReceipt(true); }} style={btnSmall()}>{t('common.cancel')}</button>
            </>
          )}
        </div>
        {phoneNumbers.length > 0 ? (
          <div style={{ border: `1px solid ${tk.border}`, borderRadius: 8, overflow: 'hidden' }}>
            {phoneNumbers.map((phone, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', gap: 12, borderBottom: index < phoneNumbers.length - 1 ? `1px solid ${tk.border}` : 'none' }}>
                <span style={{ flex: 1, fontSize: 12.5, fontFamily: '"JetBrains Mono",monospace', color: tk.text }}>{phone.number}</span>
                <button onClick={() => handleTogglePhoneReceipt(index)} disabled={editingPhone !== null} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 4, background: phone.include_in_receipt ? tk.successSoft : tk.sunken, color: phone.include_in_receipt ? tk.successText : tk.textMuted, border: 'none', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: 600 }}>
                  {phone.include_in_receipt ? t('common.yes') : t('common.no')}
                </button>
                <button onClick={() => handleEditPhone(index)} disabled={editingPhone !== null} style={{ ...btnSmall(), padding: '0 8px' }}><Edit size={12} /></button>
                <button onClick={() => handleDeletePhone(index)} disabled={editingPhone !== null} style={{ ...btnSmall('danger'), padding: '0 8px' }}><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 12 }}>{t('settings.noPhoneNumbers')}</div>
        )}
      </SettingsCard>

      {/* Social media */}
      <SettingsCard tk={tk} title={t('settings.socialMediaUrls')}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={newSocialPlatform} onChange={(e) => setNewSocialPlatform(e.target.value)} className={settingsFieldSel.className} style={{ width: 130, flex: 'none' }}>
            <option value="facebook">{t('settings.facebook')}</option>
            <option value="instagram">{t('settings.instagram')}</option>
            <option value="tiktok">{t('settings.tiktok')}</option>
            <option value="x">{t('settings.xTwitter')}</option>
          </select>
          <input type="url" value={newSocialUrl} onChange={(e) => setNewSocialUrl(e.target.value)} placeholder="https://..." maxLength={70} style={{ ...inputStyle, flex: 1, minWidth: 150 }} />
          <button onClick={() => setNewSocialIncludeReceipt((v) => !v)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0 10px', height: 36, background: newSocialIncludeReceipt ? tk.accentSoft : tk.surfaceAlt, color: newSocialIncludeReceipt ? tk.accentText : tk.textMuted, border: `1px solid ${tk.border}`, borderRadius: 7, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
            <Check size={12} style={{ opacity: newSocialIncludeReceipt ? 1 : 0.3 }} />{t('settings.includeInReceipt')}
          </button>
          {editingSocial === null ? (
            <button onClick={handleAddSocial} style={btnSmall('primary')}><Plus size={12} />{t('common.add')}</button>
          ) : (
            <>
              <button onClick={handleUpdateSocial} style={{ ...btnSmall('primary'), background: tk.success }}>{t('common.update')}</button>
              <button onClick={() => { setEditingSocial(null); setNewSocialPlatform('facebook'); setNewSocialUrl(''); setNewSocialIncludeReceipt(true); }} style={btnSmall()}>{t('common.cancel')}</button>
            </>
          )}
        </div>
        {socialMediaUrls.length > 0 ? (
          <div style={{ border: `1px solid ${tk.border}`, borderRadius: 8, overflow: 'hidden' }}>
            {socialMediaUrls.map((social, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', gap: 12, borderBottom: index < socialMediaUrls.length - 1 ? `1px solid ${tk.border}` : 'none' }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: tk.accentText, width: 70, flexShrink: 0, textTransform: 'capitalize' }}>
                  {social.platform === 'x' ? t('settings.xTwitter') : t(`settings.${social.platform}`)}
                </span>
                <span style={{ flex: 1, fontSize: 12, color: tk.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{social.url}</span>
                <button onClick={() => handleToggleSocialReceipt(index)} disabled={editingSocial !== null} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 4, background: social.include_in_receipt ? tk.successSoft : tk.sunken, color: social.include_in_receipt ? tk.successText : tk.textMuted, border: 'none', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: 600 }}>
                  {social.include_in_receipt ? t('common.yes') : t('common.no')}
                </button>
                <button onClick={() => handleEditSocial(index)} disabled={editingSocial !== null} style={{ ...btnSmall(), padding: '0 8px' }}><Edit size={12} /></button>
                <button onClick={() => handleDeleteSocial(index)} disabled={editingSocial !== null} style={{ ...btnSmall('danger'), padding: '0 8px' }}><Trash2 size={12} /></button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '16px 0', color: tk.textMuted, fontSize: 12 }}>{t('settings.noSocialMedia')}</div>
        )}
      </SettingsCard>
    </div>
    </>
  );
};

// ─── Settings (main component) ────────────────────────────────────────────────

const Settings = () => {
  const [activeSection, setActiveSection] = useState('general');
  const [copiedUserId, setCopiedUserId] = useState(null);
  const [renewingUserId, setRenewingUserId] = useState(null);
  const { user } = useSelector((state) => state.auth);
  const { mode } = useSelector((state) => state.theme);
  const usersState = useSelector((state) => state.users);
  const dispatch = useDispatch();
  const { t, isRTL, language } = useLanguage();
  const showToast = useToast();
  const showConfirm = useConfirm();
  const tk = getTokens(mode);

  const [storeData, setStoreData] = useState(null);

  // ── style helpers ──────────────────────────────────────────────────────────
  const wrapperStyle = {
    background: tk.bg, color: tk.text, padding: 20, minHeight: '100%',
    fontFamily: language === 'ar' ? '"Cairo",sans-serif' : '"DM Sans",sans-serif',
  };
  const settingsFormSel = getAdminFormSelectCSS(tk, 'set-form');
  const settingsFieldSel = getAdminFieldSelectCSS(tk, 'set-field');
  const inputStyle = {
    width: '100%', height: 40, background: tk.sunken, border: `1px solid ${tk.border}`,
    borderRadius: 8, padding: '0 12px', color: tk.text, fontFamily: 'inherit',
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = {
    fontSize: 11, fontWeight: 600, color: tk.textMuted, textTransform: 'uppercase',
    letterSpacing: 0.4, marginBottom: 6, display: 'block',
  };

  useEffect(() => { dispatch(fetchUsers(true)); }, [dispatch]);
  useEffect(() => { apiService.getStore().then((res) => setStoreData(res.data)).catch(() => setStoreData(null)); }, []);
  useEffect(() => {
    const unsubscribe = subscribeToStoreUpdates(() => {
      apiService.getStore().then((res) => setStoreData(res.data)).catch(() => setStoreData(null));
    });
    return unsubscribe;
  }, []);
  useEffect(() => {
    const unsubscribe = subscribeToUsersUpdates(() => { dispatch(fetchUsers(true)).catch(() => {}); });
    return unsubscribe;
  }, [dispatch]);

  const isAdminOrManager = isManagerOrAbove(user?.role);
  const isAdmin = isAdminOrOwner(user?.role);

  const allSections = [
    { key: 'general', label: t('settings.general'), icon: SettingsIcon },
    { key: 'users', label: t('settings.userManagement'), icon: Users },
    { key: 'barcode', label: t('settings.barcode'), icon: ScanLine },
    { key: 'about', label: t('settings.about'), icon: Info },
  ];
  const sections = allSections.filter((s) => {
    if (s.key === 'barcode' && !isAdminOrManager) return false;
    return true;
  });

  // ─── User Management ─────────────────────────────────────────────────────────
  const UserManagement = () => {
    const [showAddUser, setShowAddUser] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formState, setFormState] = useState({ full_name: '', username: '', password: '', role: '', can_discount: false });
    const [roleFilter, setRoleFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('active');
    const [usernameError, setUsernameError] = useState('');
    const [validationError, setValidationError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const { containerProps: userFormLatinKeyboardProps } = useLatinKeyboardGroupProps();
    const canManageUsers = isManagerOrAbove(user?.role);
    const isOwnerLocal = user?.role === 'owner';
    const isAdminLocal = user?.role === 'admin';
    const isManagerLocal = user?.role === 'manager';
    const allUsers = usersState.list || [];
    const users = allUsers
      .filter((u) => isUserVisibleInList(user?.role, user?.id, u))
      .filter((u) => {
        const roleMatch = isOwnerLocal
          ? roleFilter === 'all' || u.role === roleFilter
          : isAdminLocal
            ? roleFilter === 'all' || u.role === roleFilter
            : true;
        const statusMatch = statusFilter === 'all' || (statusFilter === 'active' && u.status) || (statusFilter === 'inactive' && !u.status);
        return roleMatch && statusMatch;
      })
      .sort((a, b) => (a.id === user?.id ? -1 : b.id === user?.id ? 1 : 0));
    const filteredUsers = useMemo(() => {
      const raw = userSearchQuery.trim();
      if (!raw) return users;
      const qNorm = normalizeForSearch(raw);
      if (!qNorm) return users;
      return users.filter((u) => {
        const fullName = u.full_name || '';
        const username = u.username || '';
        if (normalizeForSearch(fullName).includes(qNorm)) return true;
        if (username && normalizeForSearch(username).includes(qNorm)) return true;
        return false;
      });
    }, [users, userSearchQuery]);
    const isEditingSelf = editingUser && editingUser.id === user?.id;
    const isPasswordOnlySelfEdit = isEditingSelf && (isManagerLocal || isOwnerLocal || isAdminLocal);
    const canEditUser = (targetUser) => canManageUsers && canEditTargetUser(user?.role, user?.id, targetUser);
    const canDeleteUser = (targetUser) => canManageUsers && canDeleteTargetUser(user?.role, user?.id, targetUser);
    const canToggleStatus = (targetUser) => canManageUsers && canToggleTargetUserStatus(user?.role, user?.id, targetUser);
    const onToggleStatus = async (targetUser) => {
      if (!canToggleStatus(targetUser)) return;
      const newStatus = !targetUser.status;
      if (!await showConfirm(
        t('settings.confirmToggleStatus', {
          action: newStatus ? t('settings.confirmToggleActivateVerb') : t('settings.confirmToggleDeactivateVerb'),
          name: targetUser.full_name || '',
        }),
        {
          confirmLabel: newStatus ? t('settings.activate') : t('settings.deactivate'),
          cancelLabel: t('common.cancel') || 'Cancel',
          type: newStatus ? 'success' : 'warning',
        },
      )) return;
      try { await dispatch(updateUserThunk({ id: targetUser.id, status: newStatus })).unwrap(); } catch (err) { showToast(err.payload || t('alerts.failedToUpdateUserStatus'), "error"); }
    };
    const openAdd = () => { if (!canManageUsers) return; setFormState({ full_name: '', username: '', password: '', role: 'cashier' }); setUsernameError(''); setValidationError(''); setPasswordError(''); setShowPassword(false); setShowAddUser(true); };
    const openEdit = (u) => { if (!canManageUsers) return; setEditingUser(u); setFormState({ full_name: u.full_name || '', username: u.username || '', password: '', role: u.role || 'cashier', can_discount: u.can_discount || false }); setUsernameError(''); setValidationError(''); setPasswordError(''); setShowPassword(false); };
    const onChange = (e) => {
      const { name, value, type, checked } = e.target;
      setFormState((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
      if (name === 'username' && usernameError) setUsernameError('');
      if (name === 'password') { const len = (value || '').length; setPasswordError(len > 0 && (len < 4 || len > 30) ? t('settings.passwordLength') : ''); }
      if (validationError) setValidationError('');
    };
    const validateUserForm = (isAdd) => {
      const fn = (formState.full_name || '').trim(); const un = (formState.username || '').trim(); const pw = formState.password || '';
      if (fn.length > 40) return t('settings.fullNameMaxLength');
      if (un.length > 20) return t('settings.usernameMaxLength');
      if (isAdd) { if (pw.length < 4 || pw.length > 30) return t('settings.passwordLength'); }
      else if (isPasswordOnlySelfEdit) { if (pw.length < 4 || pw.length > 30) return t('settings.passwordLength'); }
      else if (pw && (pw.length < 4 || pw.length > 30)) { return t('settings.passwordLength'); }
      return '';
    };
    const checkUsernameExists = (username, excludeUserId = null) => {
      if (!username || !username.trim()) {
        setUsernameError('');
        return;
      }
      const un = username.toLowerCase().trim();
      const exists = allUsers.some(
        (u) => u.username.toLowerCase() === un && (excludeUserId == null || u.id !== excludeUserId),
      );
      setUsernameError(exists ? t('alerts.usernameAlreadyTaken') : '');
    };
    const onSubmitAdd = async (e) => {
      e.preventDefault(); if (!canManageUsers) return;
      const err = validateUserForm(true); if (err) { if (err === t('settings.passwordLength')) setPasswordError(err); else setValidationError(err); return; }
      checkUsernameExists(formState.username);
      const unAdd = formState.username.toLowerCase().trim();
      if (usernameError || allUsers.some((u) => u.username.toLowerCase() === unAdd)) { setUsernameError(t('alerts.usernameAlreadyTaken')); return; }
      if (!canAssignRole(user?.role, formState.role)) {
        showToast(t('settings.roleNotAllowed'), 'error');
        return;
      }
      try {
        await dispatch(createUserThunk({ full_name: formState.full_name, username: formState.username, password: formState.password, role: formState.role })).unwrap();
        setShowAddUser(false); setUsernameError('');
      } catch (err) {
        const errorMsg = err.payload || err.message || t('alerts.failedToCreateUser');
        if (errorMsg.toLowerCase().includes('username') && errorMsg.toLowerCase().includes('exists')) { setUsernameError(t('alerts.usernameAlreadyTaken')); } else { showToast(errorMsg, "error"); }
      }
    };
    const onSubmitEdit = async (e) => {
      e.preventDefault(); if (!canManageUsers || !editingUser) return;
      const err = validateUserForm(false); if (err) { if (err === t('settings.passwordLength')) setPasswordError(err); else setValidationError(err); return; }
      if (!isPasswordOnlySelfEdit) {
        const unEdit = (formState.username || '').toLowerCase().trim();
        if (allUsers.some((u) => u.username.toLowerCase() === unEdit && u.id !== editingUser.id)) {
          setUsernameError(t('alerts.usernameAlreadyTaken'));
          return;
        }
      }
      const canSetDiscount = isOwnerLocal && (formState.role === 'manager' || formState.role === 'admin');
      const payload = isPasswordOnlySelfEdit
        ? { id: editingUser.id, password: formState.password || undefined }
        : {
            id: editingUser.id,
            full_name: formState.full_name,
            username: formState.username,
            role: formState.role,
            password: formState.password || undefined,
            can_discount: canSetDiscount ? formState.can_discount : undefined,
          };
      try {
        await dispatch(updateUserThunk(payload)).unwrap();
        setEditingUser(null);
        setUsernameError('');
      } catch (err) {
        const errorMsg = err?.payload != null ? err.payload : (typeof err === 'string' ? err : err?.message || '');
        if (String(errorMsg).toLowerCase().includes('username') && String(errorMsg).toLowerCase().includes('exist')) {
          setUsernameError(t('alerts.usernameAlreadyTaken'));
        } else {
          showToast(errorMsg || t('alerts.failedToUpdateUser'), 'error');
        }
      }
    };
    const onDelete = async (id) => { if (!canManageUsers) return; if (!await showConfirm(t('alerts.deleteUserConfirm'), { confirmLabel: t('common.delete') || 'Delete', cancelLabel: t('common.cancel') || 'Cancel', type: 'danger' })) return; try { const result = await dispatch(deleteUserThunk(id)).unwrap(); if (result?.deactivated) showToast('User has transaction history and was deactivated instead of deleted.', 'warning'); } catch (err) { showToast(typeof err === 'string' ? err : (err?.message || t('alerts.failedToDeleteUser')), "error"); } };

    const rolePillStyle = (role) => ({
      display: 'inline-block', fontSize: 9.5, fontWeight: 600, padding: '2px 7px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: 0.4,
      background: role === 'owner' ? '#ede9fe' : role === 'admin' ? tk.warningSoft : role === 'manager' ? tk.accentSoft : tk.successSoft,
      color: role === 'owner' ? '#5b21b6' : role === 'admin' ? tk.warning : role === 'manager' ? tk.accentText : tk.successText,
    });
    const fieldStyle = { width: '100%', height: 36, padding: '0 12px', background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 7, color: tk.text, fontFamily: 'inherit', fontSize: 12.5, outline: 'none', boxSizing: 'border-box' };
    const fieldLabel = { fontSize: 11, fontWeight: 600, color: tk.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4, display: 'block' };

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <SettingsCard
          tk={tk}
          title={t('settings.systemUsers')}
          subtitle={`${filteredUsers.length} ${t('settings.members') || 'members'}`}
          headerRight={canManageUsers ? (
            <div style={{ marginTop: 2 }}>
              <PrimaryBtn tk={tk} icon={Plus} onClick={openAdd}>{t('settings.addUser')}</PrimaryBtn>
            </div>
          ) : null}
        >
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            {users.length > 0 && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  flex: '1 1 200px',
                  minWidth: 180,
                  height: 36,
                  padding: '0 12px',
                  background: tk.sunken,
                  border: `1px solid ${tk.border}`,
                  borderRadius: 8,
                  boxSizing: 'border-box',
                }}
              >
                <Search size={15} color={tk.textMuted} style={{ flexShrink: 0 }} />
                <input
                  type="search"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  placeholder={t('settings.searchUsersByNameOrUsername')}
                  autoComplete="off"
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                    border: 'none',
                    background: 'transparent',
                    color: tk.text,
                    fontFamily: 'inherit',
                    fontSize: 13,
                    outline: 'none',
                  }}
                />
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, flex: '1 1 280px', minWidth: 260 }}>
              {(isOwnerLocal || isAdminLocal) && (
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className={settingsFieldSel.className}
                  style={{ width: '100%', minWidth: 0, flex: 1, paddingRight: 28 }}
                >
                  <option value="all">{t('settings.allRoles')}</option>
                  {isOwnerLocal && <option value="owner">{t('settings.owner')}</option>}
                  {isOwnerLocal && <option value="admin">{t('settings.admin')}</option>}
                  <option value="manager">{t('settings.manager')}</option>
                  <option value="cashier">{t('settings.cashier')}</option>
                </select>
              )}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={settingsFieldSel.className}
                style={{ width: '100%', minWidth: 0, flex: 1, paddingRight: 28 }}
              >
                <option value="all">{t('settings.allStatuses')}</option>
                <option value="active">{t('settings.active')}</option>
                <option value="inactive">{t('settings.inactive')}</option>
              </select>
            </div>
            {(roleFilter !== 'all' || statusFilter !== 'active' || userSearchQuery.trim()) && (
              <button onClick={() => { setRoleFilter('all'); setStatusFilter('active'); setUserSearchQuery(''); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0 10px', height: 36, background: 'transparent', border: `1px solid ${tk.border}`, borderRadius: 7, color: tk.textMuted, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                <X size={11} />{t('settings.clearFilters')}
              </button>
            )}
          </div>

          {filteredUsers.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 0 10px 0', marginBottom: 4, borderBottom: `1px solid ${tk.border}` }}>
              <div style={{ width: 34 }} />
              <div style={{ flex: 1, minWidth: 0, fontSize: 10.5, fontWeight: 600, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {t('settings.fullName')}
              </div>
              <div style={{ width: 108, fontSize: 10.5, fontWeight: 600, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('settings.role')}</div>
              <div style={{ width: 106, fontSize: 10.5, fontWeight: 600, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('common.status')}</div>
              <div style={{ width: 176, textAlign: isRTL ? 'left' : 'right', fontSize: 10.5, fontWeight: 600, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4 }}>{t('common.actions')}</div>
            </div>
          )}

          {filteredUsers.map((u, i) => (
            <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '11px 0', borderBottom: i < filteredUsers.length - 1 ? `1px solid ${tk.border}` : 'none' }}>
              <AvatarInitials name={u.full_name} tk={tk} size={34} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: tk.text }}>{u.full_name}</div>
                <div style={{ fontSize: 11, color: tk.textMuted, fontFamily: '"JetBrains Mono",monospace' }}>{u.username}</div>
              </div>
              <div style={{ width: 108 }}>
                <span style={rolePillStyle(u.role)}>{t(`settings.${u.role}`)}</span>
              </div>
              <div style={{ width: 106 }}>
                <StatusPill tk={tk} kind={u.status ? 'active' : 'closed'}>{u.status ? t('settings.active') : t('settings.inactive')}</StatusPill>
              </div>
              <div style={{ width: 176, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
                {canToggleStatus(u) && (
                  <button onClick={() => onToggleStatus(u)} style={{ display: 'inline-flex', alignItems: 'center', padding: '3px 8px', borderRadius: 5, background: u.status ? tk.dangerSoft : tk.successSoft, color: u.status ? tk.dangerText : tk.successText, border: 'none', cursor: 'pointer', fontSize: 11, fontFamily: 'inherit', fontWeight: 500 }} title={u.status ? t('settings.deactivate') : t('settings.activate')}>
                    {u.status ? t('settings.deactivate') : t('settings.activate')}
                  </button>
                )}
                <button onClick={() => openEdit(u)} disabled={!canEditUser(u)} style={{ width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: tk.accentSoft, color: tk.accentText, border: 'none', cursor: canEditUser(u) ? 'pointer' : 'not-allowed', opacity: canEditUser(u) ? 1 : 0.35 }}>
                  <Edit size={12} />
                </button>
                <button onClick={() => onDelete(u.id)} disabled={!canDeleteUser(u)} style={{ width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: tk.dangerSoft, color: tk.dangerText, border: 'none', cursor: canDeleteUser(u) ? 'pointer' : 'not-allowed', opacity: canDeleteUser(u) ? 1 : 0.35 }}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}

          {users.length > 0 && filteredUsers.length === 0 && (
            <div style={{ textAlign: 'center', padding: '24px 0', color: tk.textMuted, fontSize: 12 }}>{t('settings.usersNoSearchMatches')}</div>
          )}

          {users.length === 0 && (
            <div style={{ textAlign: 'center', padding: '24px 0', color: tk.textMuted, fontSize: 12 }}>{t('common.noData')}</div>
          )}
        </SettingsCard>

        {/* Add user modal */}
        {showAddUser && (
          <ModalOverlay>
            <ModalBox tk={tk}>
              <div style={{ fontSize: 14, fontWeight: 600, color: tk.text, marginBottom: 18 }}>{t('settings.addNewUser')}</div>
              <form onSubmit={onSubmitAdd} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {validationError && <div style={{ background: tk.dangerSoft, color: tk.dangerText, border: `1px solid ${tk.dangerText}33`, borderRadius: 7, padding: '8px 12px', fontSize: 12 }}>{validationError}</div>}
                <div>
                  <label style={fieldLabel}>{t('settings.fullName')}</label>
                  <input name="full_name" value={formState.full_name} onChange={onChange} type="text" maxLength={40} style={fieldStyle} required />
                </div>
                <div {...userFormLatinKeyboardProps} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div>
                    <label style={fieldLabel}>{t('settings.username')}</label>
                    <input
                      name="username"
                      value={formState.username}
                      onChange={onChange}
                      onBlur={(e) => checkUsernameExists(e.target.value)}
                      type="text"
                      maxLength={20}
                      dir={isRTL ? 'rtl' : 'ltr'}
                      lang={language}
                      autoComplete="username"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      style={{ ...fieldStyle, border: `1px solid ${usernameError ? tk.danger : tk.border}` }}
                      required
                    />
                    {usernameError && <div style={{ fontSize: 11, color: tk.dangerText, marginTop: 4 }}>{t('settings.usernameTaken')}</div>}
                  </div>
                  <div>
                    <label style={fieldLabel}>{t('settings.password')}</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        name="password"
                        value={formState.password}
                        onChange={onChange}
                        type={showPassword ? 'text' : 'password'}
                        minLength={4}
                        maxLength={30}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        lang={language}
                        autoComplete="new-password"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        style={{ ...fieldStyle, paddingRight: 40, border: `1px solid ${passwordError ? tk.danger : tk.border}` }}
                        required
                      />
                      <button type="button" onClick={() => setShowPassword((p) => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: tk.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {passwordError && <div style={{ fontSize: 11, color: tk.dangerText, marginTop: 4 }}>{passwordError}</div>}
                  </div>
                </div>
                <div>
                  <label style={fieldLabel}>{t('settings.role')}</label>
                  <select name="role" value={formState.role} onChange={onChange} disabled={isManagerLocal} className={settingsFieldSel.className} style={{ opacity: isManagerLocal ? 0.5 : 1 }} required>
                    <option value="cashier">{t('settings.cashier')}</option>
                    {(isOwnerLocal || isAdminLocal) && <option value="manager">{t('settings.manager')}</option>}
                    {isOwnerLocal && <option value="admin">{t('settings.admin')}</option>}
                  </select>
                  {isManagerLocal && <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 4 }}>{t('settings.managersCanOnlyCreateCashiers')}</div>}
                </div>
                <div style={{ display: 'flex', gap: 8, paddingTop: 6 }}>
                  <button type="button" onClick={() => setShowAddUser(false)} style={{ flex: 1, height: 36, borderRadius: 8, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5 }}>{t('common.cancel')}</button>
                  <button type="submit" style={{ flex: 1, height: 36, borderRadius: 8, background: tk.accent, border: 'none', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600 }}>{t('settings.addUser')}</button>
                </div>
              </form>
            </ModalBox>
          </ModalOverlay>
        )}

        {/* Edit user modal */}
        {editingUser && (
          <ModalOverlay>
            <ModalBox tk={tk}>
              <div style={{ fontSize: 14, fontWeight: 600, color: tk.text, marginBottom: 18 }}>{t('settings.editUser')}</div>
              <form onSubmit={onSubmitEdit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {validationError && <div style={{ background: tk.dangerSoft, color: tk.dangerText, border: `1px solid ${tk.dangerText}33`, borderRadius: 7, padding: '8px 12px', fontSize: 12 }}>{validationError}</div>}
                {!(isPasswordOnlySelfEdit) && (
                  <div>
                    <label style={fieldLabel}>{t('settings.fullName')}</label>
                    <input name="full_name" value={formState.full_name} onChange={onChange} type="text" maxLength={40} style={fieldStyle} required />
                  </div>
                )}
                <div {...userFormLatinKeyboardProps} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {!(isPasswordOnlySelfEdit) && (
                    <div>
                      <label style={fieldLabel}>{t('settings.username')}</label>
                      <input
                        name="username"
                        value={formState.username}
                        onChange={onChange}
                        onBlur={(e) => checkUsernameExists(e.target.value, editingUser.id)}
                        type="text"
                        maxLength={20}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        lang={language}
                        autoComplete="username"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        style={{ ...fieldStyle, border: `1px solid ${usernameError ? tk.danger : tk.border}` }}
                        required
                        disabled={isPasswordOnlySelfEdit}
                      />
                      {usernameError && <div style={{ fontSize: 11, color: tk.dangerText, marginTop: 4 }}>{t('settings.usernameTaken')}</div>}
                    </div>
                  )}
                  <div>
                    <label style={fieldLabel}>{isPasswordOnlySelfEdit ? t('settings.newPassword') : t('settings.newPasswordOptional')}</label>
                    {isManagerLocal && isEditingSelf && <div style={{ fontSize: 11, color: tk.textMuted, marginBottom: 6 }}>{t('settings.managerChangePasswordHint')}</div>}
                    {isOwnerLocal && isEditingSelf && <div style={{ fontSize: 11, color: tk.textMuted, marginBottom: 6 }}>{t('settings.ownerChangePasswordHint')}</div>}
                    {isAdminLocal && isEditingSelf && <div style={{ fontSize: 11, color: tk.textMuted, marginBottom: 6 }}>{t('settings.adminChangePasswordHint')}</div>}
                    <div style={{ position: 'relative' }}>
                      <input
                        name="password"
                        value={formState.password}
                        onChange={onChange}
                        type={showPassword ? 'text' : 'password'}
                        minLength={isPasswordOnlySelfEdit ? 4 : 0}
                        maxLength={30}
                        dir={isRTL ? 'rtl' : 'ltr'}
                        lang={language}
                        autoComplete="new-password"
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        style={{ ...fieldStyle, paddingRight: 40, border: `1px solid ${passwordError ? tk.danger : tk.border}` }}
                        required={isPasswordOnlySelfEdit}
                      />
                      <button type="button" onClick={() => setShowPassword((p) => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: tk.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                    {passwordError && <div style={{ fontSize: 11, color: tk.dangerText, marginTop: 4 }}>{passwordError}</div>}
                  </div>
                </div>
                {!(isPasswordOnlySelfEdit) && (
                  <div>
                    <label style={fieldLabel}>{t('settings.role')}</label>
<select name="role" value={formState.role} onChange={onChange} required disabled={isEditingSelf || editingUser?.role === 'owner' || (isManagerLocal && editingUser?.role !== 'cashier') || (isAdminLocal && editingUser?.role === 'admin')} className={settingsFieldSel.className} style={{ opacity: (isEditingSelf || editingUser?.role === 'owner' || (isManagerLocal && editingUser?.role !== 'cashier') || (isAdminLocal && editingUser?.role === 'admin')) ? 0.5 : 1 }}>
                    <option value="cashier">{t('settings.cashier')}</option>
                    {(isOwnerLocal || isAdminLocal) && <option value="manager">{t('settings.manager')}</option>}
                    {isOwnerLocal && <option value="admin">{t('settings.admin')}</option>}
                  </select>
                    {editingUser?.role === 'owner' && <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 4 }}>{t('settings.ownerAccountProtected')}</div>}
                    {isEditingSelf && !isPasswordOnlySelfEdit && <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 4 }}>{t('settings.cannotEditSelf')}</div>}
                    {isManagerLocal && editingUser && editingUser.role !== 'cashier' && <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 4 }}>{t('settings.managersCanOnlyEditCashiers')}</div>}
                  </div>
                )}
                {!(isPasswordOnlySelfEdit) && isOwnerLocal && (formState.role === 'manager' || formState.role === 'admin') && (
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" name="can_discount" checked={!!formState.can_discount} onChange={onChange} />
                      <span style={fieldLabel}>{t('settings.canApproveDiscounts')}</span>
                    </label>
                    <div style={{ fontSize: 11, color: tk.textMuted, marginTop: 4 }}>{t('settings.canApproveDiscountsHint')}</div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, paddingTop: 6 }}>
                  <button type="button" onClick={() => { setEditingUser(null); setUsernameError(''); }} style={{ flex: 1, height: 36, borderRadius: 8, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5 }}>{t('common.cancel')}</button>
                  <button type="submit" style={{ flex: 1, height: 36, borderRadius: 8, background: tk.accent, border: 'none', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600 }}>{t('common.save')}</button>
                </div>
              </form>
            </ModalBox>
          </ModalOverlay>
        )}
      </div>
    );
  };

  // ─── Barcode Settings ────────────────────────────────────────────────────────
  const generateUUIDBarcodeSVG = (uuid, options = {}) => {
    if (!uuid || typeof document === 'undefined') return '';
    try {
      const {
        width = 1.5,
        height = 60,
        fontSize = 14,
        margin = 0,
      } = options;
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      JsBarcode(svg, uuid.toString(), { format: 'CODE128', width, height, displayValue: true, fontSize, margin, background: '#ffffff', lineColor: '#000000' });
      const serializer = new XMLSerializer();
      let svgString = serializer.serializeToString(svg);
      svgString = svgString.replace(/<svg([^>]*)>/, '<svg$1 style="max-width: 100%; width: 100%; height: auto;" preserveAspectRatio="xMidYMid meet">');
      return svgString;
    } catch (error) { console.error('Error generating barcode:', error); return ''; }
  };

  const printUUIDBarcode = (userData) => {
    const { full_name, role, refund_code } = userData;
    const barcodeValue = refund_code || '';
    if (!barcodeValue) { showToast(t('alerts.failedToGenerateBarcode'), "warning"); return; }
    const barcodeHtml = generateUUIDBarcodeSVG(barcodeValue, {
      width: 3,
      height: 120,
      fontSize: 28,
      margin: 8,
    });
    if (!barcodeHtml) { showToast(t('alerts.failedToGenerateBarcode'), "warning"); return; }
    const lang = (typeof localStorage !== 'undefined' && localStorage.getItem('language')) || 'en';
    const bt = getBarcodeT(lang);
    const roleLabel = bt('settings.role'); const roleName = bt(`settings.${role}`); const footerText = bt('settings.forRefundApproval');
    const isRTLPrint = lang === 'ar'; const fontFamily = isRTLPrint ? "'Segoe UI', 'Tahoma', 'Arial', sans-serif" : "'Courier New', monospace";
    const html = `<html dir="${isRTLPrint ? 'rtl' : 'ltr'}"><head><meta charset="UTF-8"><title>UUID Barcode - ${full_name}</title><style>@page{size:80mm auto;margin:0}body{font-family:${fontFamily};font-size:10px;margin:0;padding:0;word-break:break-word;overflow-wrap:break-word}.barcode-page{width:100%;box-sizing:border-box;padding:4px 16px;text-align:center}.header{margin-bottom:12px}.title{font-size:13px;font-weight:bold;margin-bottom:6px}.subtitle{font-size:10px;font-weight:bold;color:#000;margin-bottom:5px}.barcode-container{width:100%;margin:12px 0}.barcode-container svg{max-width:100%;height:auto;width:100%}.footer{margin-top:12px;font-size:9px;font-weight:bold;color:#000}</style></head><body><div class="barcode-page"><div class="header"><div class="title">${full_name}</div><div class="subtitle">${roleLabel}: ${roleName}</div></div><div class="barcode-container">${barcodeHtml}</div><div class="footer"><div>${footerText}</div><div>${new Date().toLocaleString(lang === 'ar' ? 'ar-EG' : 'en-US')}</div></div></div></body></html>`;
    printHtml(html);
  };

  const downloadUUIDBarcodeImage = (userData) => {
    const { full_name, refund_code } = userData;
    const barcodeValue = refund_code || '';
    if (!barcodeValue) { showToast(t('alerts.failedToGenerateBarcode'), "warning"); return; }
    const barcodeHtml = generateUUIDBarcodeSVG(barcodeValue);
    if (!barcodeHtml) { showToast(t('alerts.failedToGenerateBarcode'), "warning"); return; }

    try {
      const svgBlob = new Blob([barcodeHtml], { type: 'image/svg+xml;charset=utf-8' });
      const svgUrl = URL.createObjectURL(svgBlob);
      const image = new Image();

      image.onload = () => {
        try {
          const padding = 28;
          const width = image.width + (padding * 2);
          const height = image.height + (padding * 2);
          const pixelRatio = Math.max(2, Math.floor((window.devicePixelRatio || 1) * 2));
          const canvas = document.createElement('canvas');
          canvas.width = width * pixelRatio;
          canvas.height = height * pixelRatio;
          canvas.style.width = `${width}px`;
          canvas.style.height = `${height}px`;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            showToast(t('alerts.failedToGenerateBarcode'), "error");
            URL.revokeObjectURL(svgUrl);
            return;
          }

          ctx.scale(pixelRatio, pixelRatio);
          ctx.imageSmoothingEnabled = false;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(image, padding, padding);

          const cleanName = String(full_name || 'user')
            .trim()
            .replace(/\s+/g, '_')
            .replace(/[^a-zA-Z0-9_-]/g, '')
            .toLowerCase() || 'user';
          const link = document.createElement('a');
          link.href = canvas.toDataURL('image/png');
          link.download = `${cleanName}_refund_code_barcode.png`;
          link.click();
        } catch (err) {
          console.error('Failed to download barcode image:', err);
          showToast(t('alerts.failedToGenerateBarcode'), "error");
        } finally {
          URL.revokeObjectURL(svgUrl);
        }
      };

      image.onerror = () => {
        URL.revokeObjectURL(svgUrl);
        showToast(t('alerts.failedToGenerateBarcode'), "error");
      };

      image.src = svgUrl;
    } catch (error) {
      console.error('Failed to prepare barcode download:', error);
      showToast(t('alerts.failedToGenerateBarcode'), "error");
    }
  };

  const BarcodeSettings = () => {
    const isOwnerLocal = user?.role === 'owner';
    const isAdminLocal = user?.role === 'admin';
    const isManagerLocal = user?.role === 'manager';
    const [barcodeRoleFilter, setBarcodeRoleFilter] = useState('all');
    const [barcodeSearchQuery, setBarcodeSearchQuery] = useState('');
    const userList = usersState.list;

    const availableUsers = useMemo(() => {
      const all = userList || [];
      if (isOwnerLocal) {
        return all.filter((u) => (u.role === 'owner' || u.role === 'admin' || u.role === 'manager') && u.status !== false);
      }
      if (isAdminLocal || isManagerLocal) {
        return all.filter((u) => u.id === user?.id && u.status !== false);
      }
      return [];
    }, [isOwnerLocal, isAdminLocal, isManagerLocal, userList, user?.id]);

    const displayedUsers = useMemo(() => {
      if (!isOwnerLocal) return availableUsers;
      let list = availableUsers;
      if (barcodeRoleFilter !== 'all') list = list.filter((u) => u.role === barcodeRoleFilter);
      const raw = barcodeSearchQuery.trim();
      if (!raw) return list;
      const qNorm = normalizeForSearch(raw);
      if (!qNorm) return list;
      return list.filter((u) => {
        const fullName = u.full_name || '';
        const username = u.username || '';
        const code = String(u.refund_code || '');
        if (normalizeForSearch(fullName).includes(qNorm)) return true;
        if (username && normalizeForSearch(username).includes(qNorm)) return true;
        if (code && normalizeForSearch(code).includes(qNorm)) return true;
        return false;
      });
    }, [isOwnerLocal, availableUsers, barcodeRoleFilter, barcodeSearchQuery]);

    if (!isOwnerLocal && !isAdminLocal && !isManagerLocal) {
      return <SettingsCard tk={tk} title={t('settings.uuidBarcodePrinting')}><div style={{ fontSize: 12, color: tk.textMuted }}>{t('settings.accessDenied')}</div></SettingsCard>;
    }

    const handleRenewCode = async (u) => {
      const confirmed = await showConfirm(
        t('settings.renewCodeWarning', { name: u.full_name }),
        { confirmLabel: t('settings.confirmRenew'), cancelLabel: t('common.cancel'), type: 'danger' }
      );
      if (!confirmed) return;
      setRenewingUserId(u.id);
      try {
        await apiService.renewRefundCode(u.id);
        await dispatch(fetchUsers(true));
        showToast(t('settings.renewCodeSuccess'), 'success');
      } catch (err) {
        showToast(err.message || t('settings.renewCodeFailed'), 'error');
      } finally {
        setRenewingUserId(null);
      }
    };

    const showOwnerBarcodeFilters = isOwnerLocal && !usersState.loading && availableUsers.length > 0;
    const listSubtitle = isOwnerLocal ? t('settings.printBarcodeForAdminManager') : t('settings.printYourBarcode');

    return (
      <SettingsCard tk={tk} title={t('settings.uuidBarcodePrinting')} subtitle={listSubtitle}>
        {showOwnerBarcodeFilters && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flex: '1 1 200px',
                minWidth: 180,
                height: 36,
                padding: '0 12px',
                background: tk.sunken,
                border: `1px solid ${tk.border}`,
                borderRadius: 8,
                boxSizing: 'border-box',
              }}
            >
              <Search size={15} color={tk.textMuted} style={{ flexShrink: 0 }} />
              <input
                type="search"
                value={barcodeSearchQuery}
                onChange={(e) => setBarcodeSearchQuery(e.target.value)}
                placeholder={t('settings.searchRefundBarcodeUsers')}
                autoComplete="off"
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: '100%',
                  border: 'none',
                  background: 'transparent',
                  color: tk.text,
                  fontFamily: 'inherit',
                  fontSize: 13,
                  outline: 'none',
                }}
              />
            </div>
            <select
              value={barcodeRoleFilter}
              onChange={(e) => setBarcodeRoleFilter(e.target.value)}
              className={settingsFieldSel.className}
              style={{ width: '100%', minWidth: 160, maxWidth: 240, flex: '1 1 160px', height: 36, paddingRight: 28 }}
            >
              <option value="all">{t('settings.allRoles')}</option>
              <option value="owner">{t('settings.owner')}</option>
              <option value="admin">{t('settings.admin')}</option>
              <option value="manager">{t('settings.manager')}</option>
            </select>
            {(barcodeRoleFilter !== 'all' || barcodeSearchQuery.trim()) && (
              <button
                type="button"
                onClick={() => { setBarcodeRoleFilter('all'); setBarcodeSearchQuery(''); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0 10px', height: 36, background: 'transparent', border: `1px solid ${tk.border}`, borderRadius: 7, color: tk.textMuted, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}
              >
                <X size={11} />{t('settings.clearFilters')}
              </button>
            )}
          </div>
        )}
        {usersState.loading ? (
          <div style={{ fontSize: 12, color: tk.textMuted }}>{t('settings.loadingUsers')}</div>
        ) : availableUsers.length === 0 ? (
          <div style={{ fontSize: 12, color: tk.textMuted }}>{t('settings.noUsersAvailable')}</div>
        ) : displayedUsers.length === 0 ? (
          <div style={{ fontSize: 12, color: tk.textMuted }}>{t('settings.noRefundBarcodeFilterMatch')}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {displayedUsers.map((u) => (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, background: tk.surfaceAlt, borderRadius: 10, border: `1px solid ${tk.border}` }}>
                <AvatarInitials name={u.full_name} tk={tk} size={36} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: tk.text }}>{u.full_name}</span>
                    {(isOwnerLocal || u.id === user?.id) && (
                      <button
                        onClick={() => handleRenewCode(u)}
                        disabled={renewingUserId === u.id}
                        style={{ background: 'none', border: 'none', color: tk.textMuted, cursor: renewingUserId === u.id ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', padding: 2, opacity: renewingUserId === u.id ? 0.5 : 1, flexShrink: 0 }}
                        title={t('settings.renewCode')}
                      >
                        {renewingUserId === u.id
                          ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                          : <RefreshCw size={12} />}
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: tk.textMuted, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                    <span style={{ textTransform: 'capitalize' }}>{t(`settings.${u.role}`)}</span>
                    <span>•</span>
                    <span>{t('settings.refundCode')}:</span>
                    <span style={{ fontFamily: '"JetBrains Mono",monospace', fontSize: 11, fontWeight: 500, color: tk.text }}>{u.refund_code || '—'}</span>
                    <button onClick={() => { navigator.clipboard.writeText(u.refund_code || ''); setCopiedUserId(u.id); setTimeout(() => setCopiedUserId(null), 1500); }} style={{ background: 'none', border: 'none', color: tk.textMuted, cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 2 }} title={t('common.copy')}><Copy size={12} /></button>
                    {copiedUserId === u.id && <span style={{ fontSize: 11, color: tk.successText, fontWeight: 500 }}>{t('common.copied')}</span>}
                  </div>
                </div>
                <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button onClick={() => printUUIDBarcode(u)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '0 14px', height: 34, borderRadius: 8, background: tk.accent, color: '#fff', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500 }}>
                    <Printer size={13} />{t('settings.printBarcode')}
                  </button>
                  <button onClick={() => downloadUUIDBarcodeImage(u)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '0 12px', height: 34, borderRadius: 8, background: tk.surface, color: tk.text, border: `1px solid ${tk.border}`, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500 }}>
                    <Download size={13} />{t('settings.downloadBarcode')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SettingsCard>
    );
  };

  // ─── About ───────────────────────────────────────────────────────────────────
  const AboutSection = () => (
    <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 14, overflow: 'hidden' }}>
      <AboutContent
        embedded
        colors={{
          text: tk.text,
          textSecondary: tk.textSecondary,
          textMuted: tk.textMuted,
          border: tk.border,
          surface: tk.surface,
          surfaceAlt: tk.surfaceAlt,
          indigo: tk.accent,
          indigoLight: tk.accentSoft,
        }}
      />
    </div>
  );

  const LicenseSection = () => {
    const [licInfo, setLicInfo] = React.useState(null);
    const [licenseKey, setLicenseKey] = React.useState(null);
    const [serverMobileLink, setServerMobileLink] = React.useState(null);
    const [mobileLinkLoaded, setMobileLinkLoaded] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [copied, setCopied] = React.useState('');

    React.useEffect(() => {
      let cancelled = false;
      setLoading(true);
      Promise.all([
        apiService.get('/api/license/status').catch(() => null),
        apiService.getLicenseConfigDisplay().catch(() => null),
      ])
        .then(([statusData, cfg]) => {
          if (cancelled) return;
          setLicInfo(statusData);
          setLicenseKey(cfg?.license_key ?? null);
          setServerMobileLink(cfg?.server_mobile_link ?? null);
          setMobileLinkLoaded(true);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => { cancelled = true; };
    }, []);

    const copy = (text, key) => {
      navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 2000);
    };

    const row = (label, value, copyKey) => (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${tk.border}` }}>
        <span style={{ fontSize: 12, color: tk.textMuted, flexShrink: 0, width: 130 }}>{label}</span>
        <span style={{ fontSize: 12.5, color: tk.text, fontFamily: 'monospace', flex: 1, textAlign: 'end' }}>{value ?? '—'}</span>
        {copyKey && value && (
          <button onClick={() => copy(value, copyKey)} style={{ marginLeft: 8, padding: '2px 6px', borderRadius: 4, border: `1px solid ${tk.border}`, background: 'transparent', cursor: 'pointer', color: copied === copyKey ? tk.successText : tk.textMuted, fontSize: 11 }}>
            {copied === copyKey ? '✓' : <Copy size={11} />}
          </button>
        )}
      </div>
    );

    const statusColor = licInfo?.valid ? tk.successText : tk.dangerText;
    const statusBg = licInfo?.valid ? tk.successSoft : tk.dangerSoft;

    return (
      <>
        <SettingsCard tk={tk} title={t('settings.license')} subtitle={t('settings.licenseTabSubtitle')}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: tk.textMuted, fontSize: 12.5 }}>
              <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} />{t('settings.licenseChecking')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 20, background: statusBg, marginBottom: 16, alignSelf: 'flex-start' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
                <span style={{ fontSize: 12, fontWeight: 600, color: statusColor }}>
                  {licInfo?.valid ? (licInfo.code === 'EXPIRING_SOON' ? `Expiring soon — ${licInfo.daysRemaining}d left` : 'Active') : (licInfo?.message || 'Not activated')}
                </span>
              </div>

              {licInfo?.licenseInfo ? (
                <>
                  {row('Register ID', licInfo.licenseInfo.register_id, 'rid')}
                  {row('License Key', licenseKey, 'lkey')}
                  {row('Store', licInfo.licenseInfo.store_name)}
                  {row('Activated', licInfo.licenseInfo.activated_at ? new Date(licInfo.licenseInfo.activated_at).toLocaleDateString() : null)}
                  {row('Expires', licInfo.licenseInfo.expires_at ? new Date(licInfo.licenseInfo.expires_at).toLocaleDateString() : 'Never')}
                </>
              ) : (
                <>
                  <div style={{ fontSize: 12.5, color: tk.textMuted }}>
                    {licInfo?.message || 'No activation token found. Please activate this device.'}
                  </div>
                  {mobileLinkLoaded && row('License Key', licenseKey, 'lkey')}
                </>
              )}

              {mobileLinkLoaded && (
                serverMobileLink
                  ? row(t('settings.licenseServerMobileLink'), serverMobileLink, 'mobile')
                  : row(t('settings.licenseServerMobileLink'), t('settings.licenseServerMobileLinkNotSet'))
              )}
            </div>
          )}
        </SettingsCard>
        {isAdmin && <LicenseSlotsSettings tk={tk} t={t} />}
      </>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={wrapperStyle} className={`theme-${mode}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <style>{settingsFormSel.css}{settingsFieldSel.css}</style>
      <PageHeader
        tk={tk}
        title={t('nav.settings')}
        subtitle={language === 'ar' ? 'إعداد متجرك وفريقك والأجهزة' : 'Configure your store, team & hardware'}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 16, alignItems: 'flex-start' }}>
        {/* Left nav */}
        <div style={{ background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 12, padding: 8, height: 'fit-content', position: 'sticky', top: 16 }}>
          {sections.map((s) => (
            <button key={s.key} onClick={() => setActiveSection(s.key)} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 1, background: activeSection === s.key ? tk.accentSoft : 'transparent', color: activeSection === s.key ? tk.accentText : tk.textSecondary, border: 'none', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: activeSection === s.key ? 600 : 500, textAlign: 'start', transition: 'background 0.12s, color 0.12s' }}>
              <s.icon size={14} />
              <span>{s.label}</span>
            </button>
          ))}
        </div>

        {/* Right content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {activeSection === 'general' && storeData !== undefined && (
            <GeneralSettings storeData={storeData} tk={tk} t={t} isRTL={isRTL} />
          )}
          {activeSection === 'users' && <UserManagement />}
          {activeSection === 'barcode' && <BarcodeSettings />}
          {activeSection === 'about' && <AboutSection />}
        </div>
      </div>
    </div>
  );
};

export default Settings;
